import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Mutable Supabase client instance
let supabase: SupabaseClient;

// Function to initialize Supabase with provided credentials
export const initializeSupabase = (supabaseUrl: string, supabaseAnonKey: string) => {
  // Validate credentials
  if (!supabaseUrl || !supabaseAnonKey || 
      supabaseUrl === 'your_supabase_url_here' || 
      supabaseAnonKey === 'your_supabase_anon_key_here') {
    console.error('Supabase configuration missing or using placeholder values.');
    console.error('Please provide valid Supabase credentials to SafetyPortalApp component:');
    console.error('supabaseUrl: https://your-project-ref.supabase.co');
    console.error('supabaseAnonKey: your-actual-anon-key');
    
    throw new Error('Supabase not configured. Please check the console for setup instructions.');
  }

  supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      flowType: 'pkce'
    }
  });
};

// Export the supabase instance (will be initialized by initializeSupabase)
export { supabase };

// Database types based on actual schema
export interface Client {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  created_at: string;
  updated_at: string;
}

export interface Site {
  id: string;
  name: string;
  type: string;
  address: string;
  start_date: string;
  end_date: string;
  description?: string;
  requirements?: string[];
  photo_url?: string;
  status: 'active' | 'completed' | 'suspended';
  asbestos_type?: string;
  quote_url?: string;
  latitude?: number;
  longitude?: number;
  geofence_radius?: number;
  client_id?: string;
  client_name?: string;
  client_address?: string;
  client_phone?: string;
  principal?: string;
  created_at: string;
  updated_at: string;
}

export interface Worker {
  id: string;
  name: string;
  role: string;
  phone?: string;
  email: string;
  start_date: string;
  certifications?: string[];
  status: 'active' | 'inactive';
  whitecard_number?: string;
  requires_health_monitoring?: boolean;
  pin?: string;
  hourly_pay_rate?: number;
  overtime_pay_rate?: number;
  created_at: string;
  updated_at: string;
}

export interface Induction {
  id: string;
  site_id: string;
  worker_id: string;
  status: 'pending' | 'completed' | 'expired';
  completed_at?: string;
  signature?: string; // Can be either text or base64 image data
  worker_photo_url?: string;
  created_at: string;
  updated_at: string;
}

export interface SiteAssignment {
  id: string;
  site_id: string;
  worker_id: string;
  assigned_at: string;
  assigned_by?: string;
  status: 'active' | 'inactive';
}

export interface WorkerPhoto {
  id: string;
  worker_id: string;
  url: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectStatus {
  id: string;
  site_id: string;
  gas_approval: 'live' | 'pending' | 'disconnected';
  electrical_approval: 'live' | 'pending' | 'disconnected';
  council_approval: 'pending' | 'approved';
  epa_permit: 'pending' | 'approved' | 'not_required' | 'rejected';
  safework_notification: 'pending' | 'approved';
  temporary_fence: 'pending' | 'installed';
  air_monitoring: 'required' | 'not_required';
  soil_testing: 'required' | 'not_required';
  hygienist_required: boolean;
  hygienist_id?: string;
  hygienist_name?: string;
  hygienist_phone?: string;
  created_at: string;
  updated_at: string;
}

export interface Hygienist {
  id: string;
  name: string;
  phone: string;
  email?: string;
  company?: string;
  created_at: string;
  updated_at: string;
}

export interface SWMS {
  id: string;
  title: string;
  site_id: string;
  document_type: 'demolition_swms' | 'asbestos_swms' | 'asbestos_removal_control_plan';
  activity_description: string;
  hazards: string[];
  control_measures: string[];
  ppe_required: string[];
  emergency_procedures: string;
  approval_status: 'draft' | 'pending' | 'approved' | 'rejected';
  approved_by?: string;
  approved_date?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  version: number;
  pdf_url?: string;
}

export interface Vehicle {
  id: string;
  registration: string;
  type: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
  rego_expiry_date: string;
  status: string;
  current_location?: string;
  notes?: string;
  nhvr_scheduled_date?: string;
  nhvr_town?: string;
  nhvr_required?: boolean;
  service_interval_km?: number;
  service_interval_months?: number;
  current_km?: number;
  last_service_date?: string;
  last_service_km?: number;
  next_service_due_date?: string;
  next_service_due_km?: number;
  service_interval_hours?: number;
  current_hours?: number;
  last_service_hours?: number;
  next_service_due_hours?: number;
  created_at: string;
  updated_at: string;
}

export interface VehicleInspection {
  id: string;
  vehicle_id: string;
  inspection_type: string;
  inspection_date: string;
  expiry_date: string;
  inspector_name?: string;
  inspector_company?: string;
  certificate_number?: string;
  status: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}